import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useFomoCoupon } from "../../hooks/useFomoCoupon.js";
import { useFomoCouponTextPersistence } from "../../hooks/useFomoCouponTextPersistence.js";
import { CONSENT_API_DEFINITION_COUPON_TEXT } from "../../utils/fomoConsentDefinitions.js";
import { __ } from "../../utils/i18n.js";
import { localStorageTest } from "../../utils/localStorageTest.js";
const LOCAL_STORAGE_NAME_FOMO_COUPON_IN_APP_REF = "real-commerce-coupon-in-app";
const LOCAL_STORAGE_NAME_FOMO_COUPON_USER_OF_PRODUCT = "real-commerce-user-of-product";
const CONSENT_API_DEFINITION_IN_APP_REF = [
    "local",
    LOCAL_STORAGE_NAME_FOMO_COUPON_IN_APP_REF,
    "*"
];
const CONSENT_API_DEFINITION_USER_OF_PRODUCT = [
    "local",
    LOCAL_STORAGE_NAME_FOMO_COUPON_USER_OF_PRODUCT,
    "*"
];
const disableFomoCoupon = (obj)=>{
    if (localStorageTest()) {
        const { consentApi } = window;
        if (obj.reason === "in-app") {
            const waitConsent = consentApi?.consent(...CONSENT_API_DEFINITION_IN_APP_REF) || Promise.resolve();
            waitConsent.then(()=>{
                localStorage.setItem(LOCAL_STORAGE_NAME_FOMO_COUPON_IN_APP_REF, "1");
            }).catch(()=>{});
        } else if (obj.reason === "user-of-product") {
            const productIds = (localStorage.getItem(LOCAL_STORAGE_NAME_FOMO_COUPON_USER_OF_PRODUCT) || "").split(",").filter(Boolean);
            const sId = `${obj.productId}`;
            if (productIds.indexOf(sId) === -1) {
                productIds.push(sId);
                const waitConsent = consentApi?.consent(...CONSENT_API_DEFINITION_USER_OF_PRODUCT) || Promise.resolve();
                waitConsent.then(()=>{
                    localStorage.setItem(LOCAL_STORAGE_NAME_FOMO_COUPON_USER_OF_PRODUCT, productIds.join(","));
                }).catch(()=>{});
            }
        }
    }
};
const FomoCouponBanner = ({ product, disclaimer })=>{
    const coupon = useFomoCoupon(product);
    const { interpolatedComponent, configuration } = useFomoCouponTextPersistence(coupon, {
        interpolateCouponInPercent: /*#__PURE__*/ _jsx("strong", {
            className: "fomo-coupon-percent"
        }),
        interpolateCouponString: /*#__PURE__*/ _jsx("span", {
            className: "fomo-coupon-string"
        })
    });
    const supportsLocalStorage = useMemo(()=>localStorageTest(), []);
    const [hasFomoConsent, setHasFomoConsent] = useState(false);
    useEffect(()=>{
        const { consentApi } = window;
        if (!consentApi) {
            setHasFomoConsent(false);
            return;
        }
        const waitConsent = [
            consentApi.consent(...CONSENT_API_DEFINITION_IN_APP_REF),
            consentApi.consent(...CONSENT_API_DEFINITION_USER_OF_PRODUCT),
            consentApi.consent(...CONSENT_API_DEFINITION_COUPON_TEXT)
        ];
        Promise.all(waitConsent).then(()=>setHasFomoConsent(true)).catch(()=>setHasFomoConsent(false));
    }, []);
    const inAppRef = useMemo(()=>hasFomoConsent && (window.location.search.indexOf("&promo=in-app") > -1 || supportsLocalStorage && !!localStorage.getItem(LOCAL_STORAGE_NAME_FOMO_COUPON_IN_APP_REF)), [
        hasFomoConsent,
        supportsLocalStorage
    ]);
    const userOfProduct = useMemo(()=>hasFomoConsent && supportsLocalStorage && `,${localStorage.getItem(LOCAL_STORAGE_NAME_FOMO_COUPON_USER_OF_PRODUCT) || ""},`.indexOf(`,${product},`) > -1, [
        hasFomoConsent,
        product,
        supportsLocalStorage
    ]);
    const disabled = !hasFomoConsent || inAppRef || userOfProduct;
    useEffect(()=>{
        if (inAppRef) {
            disableFomoCoupon({
                reason: "in-app"
            });
        }
    }, [
        inAppRef
    ]);
    // Render disclaimer after a given element
    useEffect(()=>{
        const disclaimerElement = disclaimer ? document.querySelector(disclaimer) : undefined;
        if (!disabled && configuration && disclaimerElement) {
            const { coupon: couponString, valueInPercent, validUntil } = coupon;
            const element = document.createElement("p");
            element.className = "fomo-coupon-disclaimer";
            element.setAttribute("data-product", product);
            element.innerHTML = __("The coupon %s is valid until %s. Only for new customers who have not yet ordered this product. The discount of %d%% applies to the first invoicing period.", couponString, new Date(new Date(validUntil).getTime() + new Date().getTimezoneOffset() * 60 * 1000).toLocaleDateString(document.body.parentElement.getAttribute("lang")), valueInPercent);
            disclaimerElement.appendChild(element);
            return ()=>{
                element.remove();
            };
        } else {
            return ()=>{
            // Silence is golden.
            };
        }
    }, [
        disabled,
        configuration,
        coupon
    ]);
    return disabled ? null : /*#__PURE__*/ _jsx(_Fragment, {
        children: interpolatedComponent
    });
};
export { FomoCouponBanner, disableFomoCoupon };
